import { Injectable, NgZone, inject, effect } from '@angular/core';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IdentitySerializer, JsonSerializer, RSocketClient } from 'rsocket-core';
import RSocketWebSocketClientImport from 'rsocket-websocket-client';
import { MARKET_MESSAGING_WEBSOCKET_URL } from '@Env/environment';
import { AuthSessionService } from '@/core/services/auth-session.service';

/** esbuild/vite may wrap CJS default export twice */
const RSocketWebSocketClient: any =
  (RSocketWebSocketClientImport as any)?.default ?? RSocketWebSocketClientImport;

@Injectable({ providedIn: 'root' })
export class WebsocketService {
  private readonly messagesSubject$ = new Subject<unknown>();
  readonly messages$ = this.messagesSubject$.asObservable();
  readonly notificationEvents$ = this.messages$.pipe(filter(isNotificationEvent));

  private readonly authSession = inject(AuthSessionService);
  private readonly zone = inject(NgZone);

  private connected = false;
  private connecting = false;

  constructor() {
    effect(() => {
      const token = this.authSession.isAuthenticated() ? this.authSession.getToken() : null;
      if (token) {
        this.connect(token);
      }
    });
  }

  connect(token?: string): void {
    const accessToken = token || this.authSession.getToken();
    if (!accessToken) {
      console.warn('[WebsocketService] No auth token, skip connect');
      return;
    }

    if (this.connected || this.connecting) {
      return;
    }

    this.connecting = true;
    const url = MARKET_MESSAGING_WEBSOCKET_URL;
    console.info('[WebsocketService] Connecting market:', url);

    try {
      const client = new RSocketClient({
        serializers: {
          data: JsonSerializer,
          metadata: IdentitySerializer,
        },
        setup: {
          keepAlive: 60000,
          lifetime: 180000,
          dataMimeType: 'application/json',
          metadataMimeType: 'message/x.rsocket.routing.v0',
        },
        transport: new RSocketWebSocketClient({ url }),
      });

      client.connect().subscribe({
        onComplete: (socket) => {
          this.connected = true;
          this.connecting = false;
          console.info('[WebsocketService] Connected market');

          socket
            .requestStream({
              data: { token: accessToken },
              metadata: String.fromCharCode('get.manage.events.route'.length) + 'get.manage.events.route',
            })
            .subscribe({
              onComplete: () => {
                this.connected = false;
              },
              onError: (error: any) => {
                console.error('[WebsocketService] Stream error (market):', error?.message || error);
                this.connected = false;
                this.connecting = false;
              },
              onNext: (payload: any) => {
                // RSocket callbacks are outside NgZone — re-enter so toasts update the UI
                this.zone.run(() => {
                  this.messagesSubject$.next(payload?.data);
                });
              },
              onSubscribe: (subscription: any) => {
                subscription.request(2147483647);
              },
            });
        },
        onError: (error) => {
          console.error('[WebsocketService] Connect error (market):', error?.message || error);
          this.connected = false;
          this.connecting = false;
        },
      });
    } catch (error) {
      console.error('[WebsocketService] Failed to create client (market):', error);
      this.connected = false;
      this.connecting = false;
    }
  }
}

function isNotificationEvent(event: unknown): boolean {
  if (!event || typeof event !== 'object') {
    return false;
  }

  const record = event as Record<string, unknown>;
  const entityType = String(record.entityType ?? '').toUpperCase();

  if (entityType === 'NOTIFICATION') {
    return true;
  }

  if (typeof record.identifier === 'string' && /id=\d+/i.test(record.identifier)) {
    return true;
  }

  return false;
}
